import React from 'react';
import type { OrderFill, Order } from '../types';
import { toEAT } from '../utils/time';

interface FillHistoryTableProps {
  fills: OrderFill[];
  order: Order;
}

function formatTimestamp(time: string): string {
  if (!time) return '—';
  try {
    const date = new Date(time);
    if (!isNaN(date.getTime())) {
      return toEAT(date);
    }
    return time;
  } catch {
    return time;
  }
}

export default function FillHistoryTable({ fills, order }: FillHistoryTableProps) {
  if (fills.length === 0) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
        No fills yet
      </div>
    );
  }
  
  const totalValue = fills.reduce((sum, f) => sum + (f.value || (f.quantity * f.price)), 0);
  const totalFilled = fills.reduce((sum, f) => sum + f.quantity, 0);
  
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ 
        marginBottom: 8, 
        fontSize: 12, 
        fontWeight: 600, 
        color: '#e2e8f0' 
      }}>
        Fill History ({fills.length} {fills.length === 1 ? 'fill' : 'fills'})
      </div>
      
      <div style={{
        border: '1px solid #334155',
        borderRadius: 6,
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '140px 100px 100px 100px 100px 1fr',
          gap: 8,
          padding: '8px 12px',
          backgroundColor: '#0f172a',
          borderBottom: '1px solid #334155',
          fontSize: 11,
          color: '#94a3b8',
          fontWeight: 600
        }}>
          <div>Time</div>
          <div style={{ textAlign: 'right' }}>Quantity</div>
          <div style={{ textAlign: 'right' }}>Price</div>
          <div style={{ textAlign: 'right' }}>Cumulative</div>
          <div style={{ textAlign: 'right' }}>Remaining</div>
          <div style={{ textAlign: 'right' }}>Value</div>
        </div>
        
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {fills.map((fill, index) => {
            const isLatest = index === fills.length - 1;
            return (
              <div
                key={fill.fillId || `${fill.time}_${index}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '140px 100px 100px 100px 100px 1fr',
                  gap: 8,
                  padding: '8px 12px',
                  backgroundColor: isLatest ? '#1e293b' : 'transparent',
                  borderBottom: index < fills.length - 1 ? '1px solid #334155' : 'none',
                  fontSize: 11,
                  fontFamily: isLatest ? 'monospace' : 'inherit',
                  fontWeight: isLatest ? 600 : 400,
                  transition: 'background-color 0.2s'
                }}
              >
                <div style={{ color: isLatest ? '#f59e0b' : '#94a3b8' }}>
                  {formatTimestamp(fill.time)}
                </div>
                <div style={{ textAlign: 'right', color: '#e2e8f0' }}>
                  {fill.quantity.toLocaleString()}
                </div>
                <div style={{ textAlign: 'right', color: '#e2e8f0' }}>
                  {fill.price.toFixed(2)}
                </div>
                <div style={{ textAlign: 'right', color: '#94a3b8' }}>
                  {fill.cumQty.toLocaleString()}
                </div>
                <div style={{ textAlign: 'right', color: '#94a3b8' }}>
                  {fill.leavesQty.toLocaleString()}
                </div>
                <div style={{ textAlign: 'right', color: '#e2e8f0' }}>
                  {(fill.value || (fill.quantity * fill.price)).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <div style={{
        marginTop: 12,
        padding: '12px',
        backgroundColor: '#0f172a',
        borderRadius: 6,
        border: '1px solid #334155',
        fontSize: 12,
        color: '#94a3b8'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span>Total Filled:</span>
          <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{totalFilled.toLocaleString()} / {order.qty.toLocaleString()}</span>
        </div>
        {order.avgFillPrice !== undefined && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span>Average Fill Price:</span>
            <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{order.avgFillPrice.toFixed(2)}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Total Value:</span>
          <span style={{ color: '#e2e8f0', fontWeight: 600 }}>
            {totalValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </span>
        </div>
      </div>
    </div>
  );
}

